import * as React from 'react';
import { Async } from '@uifabric/utilities/lib/Async';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { now, mark, measure } from '../../utilities/performance';

/**
 * Let's get some comments about this interface and some
 * js docs comments about each of the members
 * @public
 */
export interface ILayoutRenderProps {
    onLayoutRenderComplete: (pageLoadTime: number) => void;
    isDataReadyForLayout: boolean;
    itemKey: string | undefined;
}

/**
 * @beta
 */
export interface IWithLayoutRenderCompleteOptions {
    qosName?: string;
}

/**
 * @public
 */
export type IDecorateWithLayoutRenderCompleteProps<TProps> = IDecoratedProps<TProps, {}, ILayoutRenderProps>;

/**
 * No idea what this HOC does... Can we get some detailed
 * comments here about what this is doing.
 *
 * Also, let's get some comments in the code below to make things
 * crystal clear abut what's going on.
 * @internal
 */
export function withLayoutRenderComplete(
    options: IWithLayoutRenderCompleteOptions = {}
): IComponentDecorator<{}, ILayoutRenderProps> {
    const {
        qosName
    } = options;

    return <P extends {}>(
        component: React.ComponentType<P>
    ): IDecoratedComponentType<P, {}, ILayoutRenderProps> => {
        interface ILayoutTrackerState {
            // Nothing.
        }

        const Component = component;

        class LayoutTracker extends React.PureComponent<P & ILayoutRenderProps, ILayoutTrackerState> {
            private _async: Async;

            constructor(props: P & ILayoutRenderProps) {
                super(props);

                this._async = new Async(this);

                this.state = {};

                if (this._isReady(this.props, this.state)) {
                    this._onReadyStart();
                }
            }

            public componentWillReceiveProps(nextProps: Readonly<P & ILayoutRenderProps>): void {
                if ((!this._isReady(this.props, this.state) || this.props.itemKey !== nextProps.itemKey) &&
                    this._isReady(nextProps, this.state)) {
                    this._onReadyStart();
                }
            }

            public render(): JSX.Element {
                return (
                    <Component
                        { ...this.props }
                    />
                );
            }

            public componentDidMount(): void {
                if (this._isReady(this.props, this.state)) {
                    this._onReadyEnd();
                }
            }

            public componentDidUpdate(previousProps: Readonly<P & ILayoutRenderProps>, previousState: ILayoutTrackerState): void {
                if ((!this._isReady(previousProps, previousState) || previousProps.itemKey !== this.props.itemKey) &&
                    this._isReady(this.props, this.state)) {
                    this._onReadyEnd();
                }
            }

            public componentWillUnmount(): void {
                this._async.dispose();
            }

            private _isReady(props: Readonly<P & ILayoutRenderProps>, state: ILayoutTrackerState): boolean {
                if (props.isDataReadyForLayout) {
                    return true;
                }

                return false;
            }

            private _onReadyStart(): void {
                if (qosName) {
                    mark(`Layout.${qosName}.Render start`);
                }
            }

            private _onReadyEnd(): void {
                if (qosName) {
                    mark(`Layout.${qosName}.Render end`);
                }

                this._async.setTimeout(() => {
                    this.props.onLayoutRenderComplete(now());

                    if (qosName) {
                        measure(
                            `Layout.${qosName}.Render`,
                            `Layout.${qosName}.Render start`,
                            `Layout.${qosName}.Render end`
                        );
                    }
                }, 0);
            }
        }

        // @ts-ignore
        return LayoutTracker;
    };
}
